/**
 * MatchSection — the AI-narrative match block on /grants/:id (G2A CP3).
 *
 * Rendered BELOW the Eligibility Snapshot on the detail page. The
 * snapshot's above-the-fold budget (1366×768) is non-negotiable, so
 * this section is compact until the user opts in via
 * "Compute AI Match Analysis".
 *
 * Behaviours:
 *   - Hidden when `eligibility_status === "not_currently_eligible"` —
 *     no compute button, no upgrade CTA. A hard-ineligible grant is
 *     final; wasting the user's cap on it would be actively harmful.
 *   - Fetches any cached match on mount (GET /api/grants/{id}/match).
 *   - Compute click posts to /api/grants/{id}/match.
 *     - 200 → renders passed narrative OR partial_analysis fallback
 *       ("Additional narrative analysis unavailable for this profile").
 *     - 402 `match_cap_reached` → inline pill morph with human-readable
 *       reset date + upgrade CTA. Never a modal.
 *   - Optional helper hint if `mission_statement` is empty on the
 *     user's grant profile — "Add a mission statement for a more
 *     accurate narrative match".
 *
 * Discipline 2 (score vs eligibility badge separation): this section
 * never re-renders the EligibilityBadge inline with the tier chip.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Info,
  Loader2,
  Wand2,
} from "lucide-react";
import { api, formatApiError } from "@/api/client";
import MatchTierChip from "@/components/grants/MatchTierChip";
import { toast } from "sonner";

/** Format an ISO datetime as "Aug 15" (locale-friendly short form). */
function formatResetDate(iso) {
  if (!iso) return "later this month";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "later this month";
  }
}

/**
 * Approved copy for the compliance-critical fallback message. Never
 * substitute or "sugarcoat" — the CP2 dispatch explicitly locked
 * honest limitations over fake completeness.
 */
const NARRATIVE_UNAVAILABLE_MESSAGE =
  "Additional narrative analysis unavailable for this profile.";

function ConfidenceIndicator({ confidence }) {
  if (confidence === null || confidence === undefined) return null;
  // Only rendered when the narrative actually passed both gates —
  // confidence < 0.60 rows never reach this component per section-render logic.
  // Tiered labelling below (moderate / high / very high) prevents copy
  // inflation on the boundary case (0.60-0.74).
  const pct = Math.round(confidence * 100);
  let label;
  let color;
  if (confidence >= 0.9) {
    label = "Very high confidence";
    color = "#0F9F70";
  } else if (confidence >= 0.75) {
    label = "High confidence";
    color = "#0F9F70";
  } else {
    label = "Moderate confidence";
    color = "#FBBF24";
  }
  return (
    <span
      data-testid="match-confidence-indicator"
      className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-v2-textMuted"
    >
      <span
        className="inline-block w-1.5 h-1.5 rounded-full"
        style={{ background: color }}
      />
      {label} · {pct}%
    </span>
  );
}

export default function MatchSection({
  grantId,
  eligibilityStatus,
  missionStatementSet,
}) {
  // States: 'idle' (no cached match), 'loading' (fetch in flight),
  // 'result' (cached OR fresh compute), 'computing' (POST in flight),
  // 'capped' (402 match_cap_reached morph)
  const [ui, setUi] = useState({ state: "idle", data: null, capDetail: null });

  // On mount: attempt to pull any cached match. 404 is expected.
  useEffect(() => {
    if (eligibilityStatus === "not_currently_eligible") return;
    let cancelled = false;
    setUi({ state: "loading", data: null, capDetail: null });
    api
      .get(`/grants/${grantId}/match`)
      .then((res) => {
        if (!cancelled) setUi({ state: "result", data: res.data, capDetail: null });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setUi({ state: "idle", data: null, capDetail: null });
        } else {
          setUi({ state: "idle", data: null, capDetail: null });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [grantId, eligibilityStatus]);

  async function compute(force = false) {
    setUi((s) => ({ ...s, state: "computing" }));
    try {
      const url = force
        ? `/grants/${grantId}/match?force=true`
        : `/grants/${grantId}/match`;
      const res = await api.post(url);
      setUi({ state: "result", data: res.data, capDetail: null });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 402 && detail?.code === "match_cap_reached") {
        setUi({
          state: "capped",
          data: null,
          capDetail: detail,
        });
        return;
      }
      if (status === 402 && detail?.code === "match_top_page_premium_only") {
        // Free tier hit the Top-Matches route via URL — should never happen
        // from this section, but handle defensively.
        toast.error("Top Matches is a Premium feature.");
        setUi({ state: "idle", data: null, capDetail: null });
        return;
      }
      toast.error(formatApiError(err));
      setUi({ state: "idle", data: null, capDetail: null });
    }
  }

  // Not eligible — hide the section entirely (do NOT show compute button).
  if (eligibilityStatus === "not_currently_eligible") {
    return (
      <section
        data-testid="grant-match-section-not-eligible"
        className="mt-8 rounded-3xl p-6"
        style={{
          background: "var(--v2-surface)",
          border: "1px solid var(--v2-border)",
        }}
      >
        <div className="flex items-start gap-3">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-v2-textMuted" />
          <div>
            <p className="text-sm font-semibold text-v2-text">
              AI narrative match unavailable for this grant.
            </p>
            <p className="mt-1 text-xs text-v2-textMuted leading-relaxed">
              The Eligibility Snapshot above shows one or more requirements this
              grant asks for that your profile does not currently meet. AI
              narrative analysis is skipped so we don't consume one of your
              free-tier match slots on an ineligible grant.
            </p>
          </div>
        </div>
      </section>
    );
  }

  // Cap reached — inline pill morph (per user's CP3 §4).
  if (ui.state === "capped") {
    const resetLabel = formatResetDate(ui.capDetail?.next_reset_at);
    return (
      <section
        data-testid="grant-match-section-capped"
        className="mt-8 rounded-3xl p-6"
        style={{
          background: "rgba(251,191,36,0.06)",
          border: "1px solid rgba(251,191,36,0.35)",
        }}
      >
        <p className="text-xs uppercase tracking-[0.28em] text-v2-textMuted">
          AI Match Analysis
        </p>
        <p
          data-testid="match-cap-reached-copy"
          className="mt-2 text-sm text-v2-text leading-relaxed"
        >
          You've used your 3 free AI matches this month. Next slot opens{" "}
          <span className="font-semibold text-v2-text">{resetLabel}</span>, or
          upgrade to Premium for unlimited.
        </p>
        <Link
          to="/pricing"
          data-testid="match-cap-upgrade-cta"
          className="mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-v2-background bg-v2-accent hover:bg-white transition-colors"
        >
          Upgrade to Premium <ArrowRight className="w-4 h-4" />
        </Link>
      </section>
    );
  }

  // Loading cached match on mount — brief placeholder.
  if (ui.state === "loading") {
    return (
      <section
        data-testid="grant-match-section-loading"
        className="mt-8 rounded-3xl p-6"
        style={{
          background: "var(--v2-surface)",
          border: "1px solid var(--v2-border)",
        }}
      >
        <div className="flex items-center gap-3 text-sm text-v2-textMuted">
          <Loader2 className="w-4 h-4 animate-spin" /> Checking cached match…
        </div>
      </section>
    );
  }

  // Result state — either cached or fresh.
  if (ui.state === "result" && ui.data) {
    return (
      <ResultCard
        data={ui.data}
        onRecompute={() => compute(true)}
        missionStatementSet={missionStatementSet}
      />
    );
  }

  // Idle — no cached match, show the compute CTA.
  return (
    <section
      data-testid="grant-match-section-idle"
      className="mt-8 rounded-3xl p-6"
      style={{
        background: "var(--v2-surface)",
        border: "1px solid var(--v2-border)",
      }}
    >
      <p className="text-xs uppercase tracking-[0.28em] text-v2-textMuted">
        AI Match Analysis
      </p>
      <p className="mt-2 text-sm text-v2-text leading-relaxed">
        Compute a personalised match score using your grant profile plus the
        sponsor's stated priorities. Free-tier accounts include 3 AI matches
        per rolling 30 days; Premium is unlimited.
      </p>
      {!missionStatementSet && (
        <p
          data-testid="match-mission-hint"
          className="mt-3 flex items-start gap-2 text-xs text-v2-textMuted leading-relaxed"
        >
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-v2-accent" />
          Add a mission statement to your{" "}
          <Link
            to="/grant-profile"
            data-testid="match-mission-hint-link"
            className="text-v2-accent hover:text-white"
          >
            grant profile
          </Link>{" "}
          for a more accurate narrative match.
        </p>
      )}
      <button
        type="button"
        onClick={() => compute(false)}
        disabled={ui.state === "computing"}
        data-testid="match-compute-btn"
        className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-v2-background bg-v2-accent hover:bg-white transition-colors disabled:opacity-60"
      >
        {ui.state === "computing" ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Computing…
          </>
        ) : (
          <>
            <Wand2 className="w-4 h-4" /> Compute AI Match Analysis
          </>
        )}
      </button>
    </section>
  );
}

function ResultCard({ data, onRecompute, missionStatementSet }) {
  const {
    match_tier,
    match_tier_label,
    match_score,
    narrative_strengths = [],
    narrative_gaps = [],
    llm_confidence,
    llm_fail_reason,
    computed_at,
  } = data;

  const passed = match_tier !== "partial_analysis" && narrative_strengths.length > 0;
  const isPartial = match_tier === "partial_analysis";

  return (
    <section
      data-testid="grant-match-section-result"
      className="mt-8 rounded-3xl p-6"
      style={{
        background: "var(--v2-surface)",
        border: "1px solid var(--v2-border)",
      }}
    >
      <header className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs uppercase tracking-[0.28em] text-v2-textMuted">
          AI Match Analysis
        </p>
        <div className="flex items-center gap-3">
          {passed && <ConfidenceIndicator confidence={llm_confidence} />}
          <MatchTierChip
            tier={match_tier}
            score={match_score}
            size="lg"
            testId="match-section-tier-chip"
          />
        </div>
      </header>

      {passed && (
        <>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <p className="text-xs uppercase tracking-[0.14em] text-v2-textMuted flex items-center gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                Where the profile appears to align
              </p>
              <ul
                data-testid="match-strengths-list"
                className="mt-2 space-y-1.5 text-sm text-v2-text leading-relaxed"
              >
                {narrative_strengths.map((s, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-v2-accent flex-shrink-0">•</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.14em] text-v2-textMuted flex items-center gap-2">
                <AlertCircle className="w-3.5 h-3.5 text-v2-accent" />
                Considerations to strengthen
              </p>
              <ul
                data-testid="match-gaps-list"
                className="mt-2 space-y-1.5 text-sm text-v2-text leading-relaxed"
              >
                {narrative_gaps.map((g, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-v2-accent flex-shrink-0">•</span>
                    <span>{g}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      )}

      {isPartial && (
        <div
          data-testid="match-partial-analysis-message"
          className="mt-4 flex items-start gap-2 rounded-2xl p-4"
          style={{
            background: "rgba(148,163,184,0.06)",
            border: "1px solid rgba(148,163,184,0.24)",
          }}
        >
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-v2-textMuted" />
          <p className="text-sm text-v2-textMuted leading-relaxed">
            {NARRATIVE_UNAVAILABLE_MESSAGE}
            {!missionStatementSet && (
              <>
                {" "}
                Adding a mission statement to your{" "}
                <Link
                  to="/grant-profile"
                  data-testid="match-partial-mission-link"
                  className="text-v2-accent hover:text-white"
                >
                  grant profile
                </Link>{" "}
                often improves narrative confidence on a recompute.
              </>
            )}
          </p>
        </div>
      )}

      <footer className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <p className="text-[11px] text-v2-textMuted">
          Computed {new Date(computed_at).toLocaleString()}
        </p>
        <button
          type="button"
          onClick={onRecompute}
          data-testid="match-recompute-btn"
          className="text-xs text-v2-textMuted hover:text-v2-text transition-colors"
        >
          Recompute
        </button>
      </footer>
    </section>
  );
}
